// src/modules/exam/components/ExamFinish.jsx
import React, {useEffect, useMemo, useState} from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {useExam} from "../../contexts/ExamContext";
import { StudentExamService } from "../../service/StudentExamService";

const ExamFinish = ({ onClose }) => {
    const { exam, studentExamId, studentAnswers = [] } = useExam();

    const [showReview, setShowReview] = useState(false);
    // removed separate modes; we now show DataTable + preview together
    const [loading, setLoading] = useState(false);
    const [studentExam, setStudentExam] = useState(null);
    const [reviewSnap, setReviewSnap] = useState({ questionHtmlOverrides: {}, optionHtmlOverrides: {}, disabledOptions: {} });
    const [activeIdx, setActiveIdx] = useState(0);

    const studentExamServiceInstance = useMemo(() => new StudentExamService(), []);

    useEffect(() => {
        if (!studentExamId) return;
        let mounted = true;
        setLoading(true);
        studentExamServiceInstance.findById(studentExamId)
            .then((response) => { if (mounted) setStudentExam(response || null); })
            .finally(() => mounted && setLoading(false));
        // load review snapshot saved at finish time
        try {
            const raw = localStorage.getItem(`examReview:${studentExamId}`);
            if (raw) {
                const snap = JSON.parse(raw);
                setReviewSnap({
                    questionHtmlOverrides: snap?.questionHtmlOverrides || {},
                    optionHtmlOverrides: snap?.optionHtmlOverrides || {},
                    disabledOptions: snap?.disabledOptions || {},
                });
            } else {
                setReviewSnap({ questionHtmlOverrides: {}, optionHtmlOverrides: {}, disabledOptions: {} });
            }
        } catch {
            setReviewSnap({ questionHtmlOverrides: {}, optionHtmlOverrides: {}, disabledOptions: {} });
        }
        return () => { mounted = false; };
    }, [studentExamId, studentExamServiceInstance]);

    // rows derived: prefer backend snapshot studentExam.studentAnswerList (immutable finish state)
    const rows = useMemo(() => {
        const backendList = Array.isArray(studentExam?.studentAnswerList) ? studentExam.studentAnswerList : null;
        const base = backendList && backendList.length > 0 ? backendList : (studentAnswers || []);
        return base.sort((a,b)=> (Number(a?.objectIndex)||0)-(Number(b?.objectIndex)||0));
    }, [studentExam, studentAnswers]);

    // ensure activeIdx valid when data or panel state changes
    useEffect(() => {
        if (!showReview) return;
        if (rows.length === 0) { setActiveIdx(0); return; }
        if (activeIdx >= rows.length) setActiveIdx(0);
    }, [showReview, rows, activeIdx]);

    // süre (mm:ss)
    const durationRaw = studentExam?.duration ?? 0;
    const durationSeconds = durationRaw > 100000 ? Math.round(durationRaw / 1000) : durationRaw;
    const mm = String(Math.floor(durationSeconds / 60)).padStart(2, "0");
    const ss = String(durationSeconds % 60).padStart(2, "0");

    // yardımcılar
    const toLetter = (i) => String.fromCharCode(65 + i);
    const letterForOptionId = (question, id) => {
        const options = question?.optionList || [];
        const i = options.findIndex((o) => Number(o?.id) === Number(id));
        return i >= 0 ? toLetter(i) : (id ?? "-");
    };

    const isCorrectForRow = (row) => {
        const qType = row?.question?.questionType || row?.question?.type || '';
        if (qType === 'OpenEnded') return null;
        const options = row?.question?.optionList || [];
        const correctIds = options.filter((o) => o?.isCorrect).map((o) => Number(o?.id));
        const selected = Array.isArray(row?.selectedOptionIds)
            ? row.selectedOptionIds.map(Number)
            : (row?.selectedOptionId != null ? [Number(row.selectedOptionId)] : []);
        const setEq = (xs, ys) => xs.length === ys.length && xs.every((x) => ys.includes(x));
        return setEq([...(selected||[])].sort(), [...(correctIds||[])].sort());
    };

    const tableStyle = {width: "100%", borderCollapse: "collapse"};
    const thClass = "border-1 surface-border p-2 w-2 font-medium text-900 surface-100";
    const tdClass = "border-1 surface-border p-2 text-lg font-bold";

    const activeRow = rows?.[activeIdx] || null;
    const activeQuestion = activeRow?.question || null;
    const activeDisplayNumber = activeRow?.objectIndex ?? (activeIdx + 1);

    const getSelectedIds = (row) => {
        if (!row) return [];
        if (Array.isArray(row.selectedOptionIds)) return row.selectedOptionIds.map(Number);
        if (row?.selectedOptionId != null) return [Number(row.selectedOptionId)];
        return [];
    };

    // Boş cevap kontrolü
    const isBlankAnswer = (row) => {
        const qType = row?.question?.questionType || row?.question?.type || '';
        if (qType === 'OpenEnded') {
            return !(row?.answerText && String(row.answerText).trim());
        }
        const selected = getSelectedIds(row);
        return !selected || selected.length === 0;
    };

    const selectedIds = (() => {
        if (!activeRow) return [];
        if (Array.isArray(activeRow.selectedOptionIds)) return activeRow.selectedOptionIds.map(Number);
        if (activeRow?.selectedOptionId != null) return [Number(activeRow.selectedOptionId)];
        return [];
    })();
    const correctIds = Array.isArray(activeQuestion?.optionList)
        ? activeQuestion.optionList.filter(o => o?.isCorrect).map(o => Number(o?.id))
        : [];

    const optionOverrideHtml = (qId, optId, fallback) => {
        const html = reviewSnap?.optionHtmlOverrides?.[qId]?.[optId];
        return html || fallback || "";
    };

    const isEliminated = (qId, optId) => !!reviewSnap?.disabledOptions?.[qId]?.[optId];

    const qHtml = (activeQuestion && (
        reviewSnap?.questionHtmlOverrides?.[activeQuestion.id] || activeQuestion?.question || ""
    )) || "";

    const selectedLabel = (() => {
        const q = activeQuestion || {};
        const qType = q?.questionType || q?.type || '';
        if (qType === 'OpenEnded') return activeRow?.answerText?.trim() || '-';
        if (qType === 'TrueFalse') {
            const options = Array.isArray(q.optionList) ? q.optionList : [];
            const firstSel = selectedIds.length ? selectedIds[0] : null;
            const opt = firstSel != null ? options.find(o => Number(o?.id) === firstSel) : null;
            return opt && (opt.answer ?? opt.text ?? opt.title) ? (opt.answer ?? opt.text ?? opt.title) : '-';
        }
        return selectedIds.length ? selectedIds.map((id) => letterForOptionId(q, id)).join(', ') : '-';
    })();

    const correctLabel = (() => {
        const q = activeQuestion || {};
        const qType = q?.questionType || q?.type || '';
        if (qType === 'OpenEnded') return '-';
        const options = Array.isArray(q.optionList) ? q.optionList : [];
        if (qType === 'TrueFalse') {
            const co = options.find(o => o?.isCorrect);
            return co && (co.answer ?? co.text ?? co.title) ? (co.answer ?? co.text ?? co.title) : '-';
        }
        return correctIds.length ? correctIds.map((id) => letterForOptionId(q, id)).join(', ') : '-';
    })();

    return (
        <Card className="shadow-1 surface-card p-2 border-round-xl" style={{width: "75%", minHeight: "400px"}}>
            <div className="flex flex-column align-items-center justify-content-between">
                <h5 className="font-bold mb-0">Sınav Tamamlandı. İyi Günler</h5>
            </div>

            <Divider className="my-2"/>

            <div className="flex flex-row align-items-center justify-content-between">
                <h6 className="text-700 m-0">Sınav Sonucunuz</h6>
                {typeof studentExam?.score !== 'undefined' && (
                    <h6 className="text-700 m-0">
                        Puan: {studentExam?.score}
                        {typeof studentExam?.passed === 'boolean' && (
                            <span className={studentExam?.passed ? 'text-green-600 ml-2' : 'text-red-600 ml-2'}>
                                {studentExam?.passed ? 'Başarılı' : 'Başarısız'}
                            </span>
                        )}
                    </h6>
                )}
                <h6 className="text-700 m-0">Süre: {mm}:{ss}</h6>
            </div>
            <Divider className="my-2"/>

            <div className="mb-2">
                <table style={tableStyle}>
                    <thead>
                    <tr>
                        <th className={thClass}>Test Adı</th>
                        <th className={thClass}>Toplam</th>
                        <th className={thClass}>Doğru</th>
                        <th className={thClass}>Yanlış</th>
                        <th className={thClass}>Boş</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td className={tdClass}>
                            <div className="text-900">{exam?.title ?? "-"}</div>
                        </td>
                        <td className={tdClass}>
                            <div className="text-900">{studentExam?.total ?? 0}</div>
                        </td>
                        <td className={tdClass}>
                            <div className="text-green-600">{studentExam?.correct ?? 0}</div>
                        </td>
                        <td className={tdClass}>
                            <div className="text-red-600">{studentExam?.wrong ?? 0}</div>
                        </td>
                        <td className={tdClass}>
                            <div className="text-600">{studentExam?.skipped ?? 0}</div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <Divider className="my-2"/>

            <div className="flex gap-2 justify-content-end">
                <Button
                    type="button"
                    label={showReview ? "Önizlemeyi Gizle" : "Gözden Geçir"}
                    icon={showReview ? "pi pi-eye-slash" : "pi pi-eye"}
                    onClick={() => setShowReview(!showReview)}
                    outlined
                    disabled={loading}
                    size="small"
                />
                <Button
                    type="button"
                    label="Kapat"
                    icon="pi pi-times"
                    severity="danger"
                    onClick={() => {
                        try { if (studentExamId) localStorage.removeItem(`examReview:${studentExamId}`); } catch {}
                        onClose?.();
                    }}
                    size="small"
                />
            </div>

            <Divider className="my-2"/>

            {showReview && (
                <div className="flex gap-3" style={{minHeight: '30vh'}}>
                    <style>{`.user-highlight { background: #fde68a; color: #000; padding: 0 .05rem; } .opt-eliminated { opacity: .5; filter: grayscale(0.3); }`}</style>

                    <div className="flex flex-column" style={{ width: '50%' }}>
                        <DataTable
                            value={rows}
                            dataKey="question.id"
                            selectionMode="single"
                            selection={rows?.[activeIdx] || null}
                            onSelectionChange={(e) => {
                                const sel = e.value;
                                if (!sel) return;
                                const idx = rows.findIndex(r => r?.question?.id === sel?.question?.id);
                                if (idx >= 0) setActiveIdx(idx);
                            }}
                            scrollable
                            scrollHeight="40vh"
                            emptyMessage={"Kayıt Bulunmadı."}
                            rowClassName={(row) => {
                                const qType =  row?.question?.questionType || row?.question?.type || '';
                                const blank = isBlankAnswer(row);
                                if (qType === 'OpenEnded') {
                                    return blank ? { 'bg-yellow-100': true } : {};
                                }
                                if (blank) return { 'bg-yellow-100': true };
                                const c = isCorrectForRow(row);
                                return {
                                    'bg-green-100': c === true,
                                    'bg-red-100': c === false
                                };
                            }}
                        >
                            <Column header="Soru No" body={(row, {rowIndex}) => row?.objectIndex ?? (rowIndex + 1)} />
                            <Column header="Yanıtınız" body={(row) => {
                                const q = row?.question || {};
                                const qType = row?.question?.questionType || row?.question?.type || '';
                                if (qType === 'OpenEnded') return row?.answerText?.trim() || '-';
                                if (qType === 'TrueFalse') {
                                    const options = Array.isArray(q.optionList) ? q.optionList : [];
                                    const selectedArray = Array.isArray(row?.selectedOptionIds) ? row.selectedOptionIds : [];
                                    const firstSel = selectedArray.length ? selectedArray[0] : (row?.selectedOptionId ?? null);
                                    const selId = firstSel != null ? Number(firstSel) : null;
                                    const opt = selId != null ? options.find(o => Number(o?.id) === selId) : null;
                                    return opt && (opt.answer ?? opt.text ?? opt.title) ? (opt.answer ?? opt.text ?? opt.title) : '-';
                                }
                                const selected = Array.isArray(row?.selectedOptionIds) ? row.selectedOptionIds : [];
                                if (selected.length) return selected.map((id) => letterForOptionId(q, id)).join(', ');
                                return row?.answerText ?? '-';
                            }} />
                            <Column header="Anahtar" body={(row) => {
                                const q = row?.question || {};
                                const qType = row?.question?.questionType || row?.question?.type || '';
                                if (qType === 'OpenEnded') return '-';
                                const options = Array.isArray(q.optionList) ? q.optionList : [];
                                const correctOpts = options.filter(o=>o?.isCorrect);
                                if (qType === 'TrueFalse') {
                                    const co = correctOpts[0];
                                    return co && (co.answer ?? co.text ?? co.title) ? (co.answer ?? co.text ?? co.title) : '-';
                                }
                                const correctIds = correctOpts.map(o=>o?.id);
                                return correctIds.length ? correctIds.map((id) => letterForOptionId(q, id)).join(', ') : '-';
                            }} />
                            <Column header="Sonuç" body={(row) => {
                                const qType = row?.question?.questionType || row?.question?.type || '';
                                const blank = isBlankAnswer(row);
                                if (blank) return 'Boş';
                                if (qType === 'OpenEnded') return '-';
                                const r = isCorrectForRow(row);
                                return r ? 'Doğru' : 'Yanlış';
                            }} />
                        </DataTable>
                    </div>

                    <div className="flex-1 overflow-auto border-1 surface-border border-round p-3" style={{maxHeight: '40vh'}}>
                        {activeRow && activeQuestion ? (
                            <div className="flex flex-column gap-3">
                                <div className="flex flex-row gap-3 align-items-center">
                                    <span className="font-bold">Soru {activeDisplayNumber}</span>
                                    <span className="text-700">Yanıtınız: <b>{selectedLabel}</b></span>
                                    <span className="text-700">Anahtar: <b>{correctLabel}</b></span>
                                </div>
                                <div className="text-lg" dangerouslySetInnerHTML={{ __html: qHtml }} />
                                {Array.isArray(activeQuestion?.optionList) && activeQuestion.optionList.length > 0 && (
                                    <div className="flex flex-column gap-2 mt-2">
                                        {activeQuestion.optionList
                                            .slice()
                                            .sort((a,b)=> (Number(a?.objectIndex)||0) - (Number(b?.objectIndex)||0))
                                            .map((opt, idx) => {
                                                const qId = activeQuestion.id;
                                                const id = Number(opt?.id);
                                                const isSel = selectedIds.includes(id);
                                                const isCor = !!opt?.isCorrect;
                                                const eliminated = isEliminated(qId, id);
                                                const base = 'border-1 surface-border border-round p-2';
                                                const color = isSel ? (isCor ? 'bg-green-100 border-green-400' : 'bg-red-100 border-red-400') : (isCor ? 'bg-green-100 border-green-400' : '');
                                                const classes = `${base} ${color} ${eliminated ? 'opt-eliminated' : ''}`;
                                                const html = optionOverrideHtml(qId, id, (opt.answer ?? opt.text ?? opt.title ?? ''));
                                                return (
                                                    <div key={id || idx} className={classes}>
                                                        <div className="flex align-items-center gap-2">
                                                            <span className="font-bold text-700" style={{minWidth:'1.5rem'}}>{toLetter(idx)}</span>
                                                            <div className="flex-1" dangerouslySetInnerHTML={{ __html: html }} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-700">Önizleme için bir sorunun üzerine gelin.</div>
                        )}
                    </div>
                </div>
            )}
        </Card>
    );
};

export default ExamFinish;
